import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import Navbar from '../components/Navbar';
import { ToastContainer } from '../components/Toast';
import { useToast } from '../hooks/useToast';
import '../styles/SubscriptionPage.css';
import '../styles/Extras.css';
import { fmtARS } from '../lib/money';

// ── Modal confirmación eliminar cuenta ──────────────────────
function DeleteAccountModal({ onClose, onConfirm, hasPassword }) {
  const [confirmText, setConfirmText] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (confirmText !== 'ELIMINAR MI CUENTA') { setErr('Escribí exactamente: ELIMINAR MI CUENTA'); return; }
    if (hasPassword && !password) { setErr('Ingresá tu contraseña para confirmar.'); return; }
    setLoading(true);
    setErr('');
    try {
      await onConfirm(confirmText, password);
    } catch (e) {
      setErr(e.message || 'Error al eliminar la cuenta.');
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-danger-icon">⚠️</div>
        <h2 className="modal-title">Eliminar cuenta definitivamente</h2>
        <p className="modal-desc">
          Esta acción es <strong>permanente e irreversible</strong>. Se eliminarán:
          tu perfil, fotos, conexiones, mensajes y toda tu información personal.
        </p>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Escribí <strong>ELIMINAR MI CUENTA</strong> para confirmar</label>
            <input
              className="form-input"
              type="text"
              value={confirmText}
              onChange={e => setConfirmText(e.target.value)}
              placeholder="ELIMINAR MI CUENTA"
              autoComplete="off"
            />
          </div>
          {hasPassword && (
            <div className="form-group">
              <label className="form-label">Tu contraseña actual</label>
              <input
                className="form-input"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Contraseña"
              />
            </div>
          )}
          {err && <p className="form-error">{err}</p>}
          <div className="modal-actions">
            <button type="button" className="btn btn-outline" onClick={onClose} disabled={loading}>Cancelar</button>
            <button
              type="submit"
              className="btn"
              style={{ background: 'rgba(230,57,70,0.9)', color: 'white', border: 'none' }}
              disabled={loading || confirmText !== 'ELIMINAR MI CUENTA'}
            >
              {loading ? '⏳ Eliminando...' : '🗑️ Eliminar mi cuenta'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Modal reportar problema ──────────────────────────────────
function AppReportModal({ onClose, onSubmit }) {
  const [type, setType] = useState('error');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const TIPOS = [
    { value: 'error', label: '🐛 Error o falla' },
    { value: 'vulnerabilidad', label: '🔒 Vulnerabilidad de seguridad' },
    { value: 'sugerencia', label: '💡 Sugerencia de mejora' },
    { value: 'otro', label: '📝 Otro' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (description.trim().length < 10) { setErr('Describí el problema con al menos 10 caracteres.'); return; }
    setLoading(true); setErr('');
    try {
      await onSubmit({ type, description: description.trim(), urlContext: window.location.pathname });
      onClose();
    } catch (e) { setErr(e.message || 'Error enviando el reporte.'); setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <h2 className="modal-title">📣 Reportar un problema</h2>
        <p className="modal-desc">Ayudanos a mejorar la app. Tu reporte llegará directamente al equipo.</p>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Tipo de reporte</label>
            <select className="form-input" value={type} onChange={e => setType(e.target.value)}>
              {TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Descripción <span className="label-hint">({description.length}/1000)</span></label>
            <textarea
              className="form-input bio-input"
              style={{ minHeight: 120 }}
              value={description}
              onChange={e => setDescription(e.target.value.slice(0, 1000))}
              placeholder="Describí detalladamente el problema, qué hiciste, qué esperabas y qué pasó..."
            />
          </div>
          {err && <p className="form-error">{err}</p>}
          <div className="modal-actions">
            <button type="button" className="btn btn-outline" onClick={onClose} disabled={loading}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? '⏳ Enviando...' : '📤 Enviar reporte'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Página principal ─────────────────────────────────────────
export default function SubscriptionPage() {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toasts, success, error } = useToast();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [compras, setCompras] = useState(null);
  const [comprando, setComprando] = useState(null);

  useEffect(() => {
    api.paymentStatus().then(setStatus).catch(()=>{});
    api.compras().then(setCompras).catch(()=>{});
    const plan = searchParams.get('plan');
    const compra = searchParams.get('compra');
    const result = window.location.pathname;
    if (result.includes('/pago/exitoso') && compra) {
      success('¡Compra aprobada! Ya podés usarla 🎉');
      refreshUser();
    } else if (result.includes('/pago/exitoso') && plan) {
      success(`¡Plan ${plan} activado! Bienvenido a la experiencia premium 🧉`);
      refreshUser();
    } else if (result.includes('/pago/pendiente')) {
      success('Tu pago quedó pendiente. Apenas Mercado Pago lo apruebe se activa solo y te avisamos 🧉');
    } else if (result.includes('/pago/fallido')) {
      error('El pago no se pudo procesar. Intentá nuevamente.');
    }
  }, []); // eslint-disable-line

  const handleSubscribe = async (plan) => {
    setLoading(true);
    try {
      const data = await api.createPreference(plan);
      const url = process.env.NODE_ENV === 'production' ? data.initPoint : data.sandboxInitPoint;
      window.location.href = url;
    } catch(e_) {
      error(e_.message || 'Error procesando el pago.');
      setLoading(false);
    }
  };

  const handleComprar = async (producto) => {
    setComprando(producto);
    try {
      const data = await api.comprar(producto);
      window.location.href = process.env.NODE_ENV === 'production' ? data.initPoint : data.sandboxInitPoint;
    } catch (e_) {
      error(e_.message || 'Error procesando el pago.');
      setComprando(null);
    }
  };

  const activoCompra = (id) => {
    const a = compras?.activos;
    if (!a) return null;
    const hasta = (s) => new Date(String(s).replace(' ', 'T') + 'Z').toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' });
    if (id === 'boost_30' && a.boost_until) return `Activo hasta ${hasta(a.boost_until)}`;
    if (id === 'super_5' && a.super_likes_comprados > 0) return `Te quedan ${a.super_likes_comprados}`;
    if (id === 'visitas_hoy' && a.visitas_hasta) return `Activo hasta ${hasta(a.visitas_hasta)}`;
    return null;
  };
  const ICONOS_COMPRA = { boost_30: '⚡', super_5: '💘', visitas_hoy: '👁️' };

  const handleDowngrade = async () => {
    if (!window.confirm('¿Bajarte al plan gratuito? Perdés los beneficios premium inmediatamente.')) return;
    try {
      await api.cancelSubscription();
      await refreshUser();
      success('Cambiaste al plan gratuito.');
      api.paymentStatus().then(setStatus).catch(()=>{});
    } catch(e_) { error(e_.message); }
  };

  const handleDeleteAccount = async (confirmText, password) => {
    await api.deleteAccount({ confirmText, password: password || undefined });
    await logout();
    navigate('/');
  };

  const handleAppReport = async (data) => {
    await api.appReport(data);
    success('¡Gracias! Tu reporte fue enviado al equipo. 🙏');
  };

  const planes = [
    {
      id: 'free', nombre: 'Plan Gratuito', precio: 0, icono: '🌱',
      features: [
        '20 me gusta por día',
        '1 foto de perfil',
        'Chat con tus conexiones',
        '50 mensajes de IA por semana',
        'Perfil verificado',
        '3 visitas de perfil por semana',
      ],
    },
    {
      id: 'mate', nombre: 'Plan Mate', precio: status?.preciosFundador?.mate || status?.precios?.mate || 4990, precioAntes: status?.preciosFundador ? status?.precios?.mate : null, icono: '☕',
      features: [
        'Me gusta ilimitados',
        'Hasta 3 fotos',
        '3 flechazos por semana',
        'Deshacer el último descarte',
        'Guiños ilimitados',
        '1.000 mensajes de IA por semana',
        '10 visitas de perfil por semana',
        'Perfiles destacados',
        '🛵 Cupido Delivery: regalos para tus conexiones',
        'Sin anuncios',
      ],
      popular: true,
    },
    {
      id: 'premium', nombre: 'Plan Premium', precio: status?.preciosFundador?.premium || status?.precios?.premium || 9990, precioAntes: status?.preciosFundador ? status?.precios?.premium : null, icono: '🧉',
      features: [
        'Todo lo del Plan Mate',
        '10 flechazos por semana',
        'Ver quién te dio me gusta',
        'IA ilimitada',
        'Ver última conexión',
        'Visitas de perfil ilimitadas',
        'Hasta 10 fotos',
        '🛡️ Cita segura: botón de ayuda en tus citas',
        '⚡ Impulso semanal: 30 minutos primero en Explorar',
        'Soporte prioritario',
      ],
    },
  ];

  const currentPlanIndex = planes.findIndex(p => p.id === user?.plan);

  return (
    <div className="subscription-page">
      <Navbar/>
      <ToastContainer toasts={toasts}/>

      {showDeleteModal && (
        <DeleteAccountModal
          hasPassword={!user?.oauth_provider}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteAccount}
        />
      )}
      {showReportModal && (
        <AppReportModal
          onClose={() => setShowReportModal(false)}
          onSubmit={handleAppReport}
        />
      )}

      <div className="sub-container container">
        <div className="sub-header">
          <h1 className="sub-title">Elegí tu plan</h1>
          <p className="sub-sub">Empezá gratis y mejorá cuando quieras. Cancelá en cualquier momento.</p>
        </div>

        {/* Banner plan actual */}
        {status && user?.plan !== 'free' && (
          <div className="current-plan-banner">
            <div className="current-plan-info">
              <span>✅ Estás en el <strong>{status.planNombre}</strong></span>
              {status.expiresAt && (
                <span className="expires">Vence: {new Date(status.expiresAt).toLocaleDateString('es-AR')}</span>
              )}
            </div>
            <div className="current-plan-actions">
              {/* Cambiar de premium a mate */}
              {user?.plan === 'premium' && (
                <button className="btn btn-sm btn-outline" onClick={() => handleSubscribe('mate')} disabled={loading}>
                  ☕ Cambiar a Plan Mate
                </button>
              )}
              {/* Bajar al plan gratuito */}
              <button
                className="btn btn-sm"
                style={{ borderColor: 'var(--rojo)', color: 'var(--rojo-claro)', background: 'rgba(230,57,70,0.08)' }}
                onClick={handleDowngrade}
              >
                🌱 Bajar a plan gratuito
              </button>
            </div>
          </div>
        )}

        {/* Grilla de planes */}
        <div className="planes-grid">
          {planes.map((plan, idx) => {
            const isActive = user?.plan === plan.id;
            const isUpgrade = idx > currentPlanIndex;
            const isDowngrade = idx < currentPlanIndex;

            return (
              <div key={plan.id} className={`plan-card ${plan.popular ? 'plan-popular' : ''} ${isActive ? 'plan-active' : ''}`}>
                {plan.popular && !isActive && <div className="plan-badge-popular">⭐ Más elegido</div>}
                {isActive && <div className="plan-badge-current">✅ Tu plan actual</div>}
                <div className="plan-icon">{plan.icono}</div>
                <h2 className="plan-nombre">{plan.nombre}</h2>
                <div className="plan-precio">
                  {plan.precio === 0
                    ? <span className="precio-gratis">Gratis</span>
                    : <>
                        {plan.precioAntes && <span style={{ textDecoration: 'line-through', opacity: .55, marginRight: 8, fontSize: 15 }}>${plan.precioAntes.toLocaleString('es-AR')}</span>}
                        <span className="precio-valor">${plan.precio.toLocaleString('es-AR')}</span><span className="precio-per">/mes</span>
                        {plan.precioAntes && <div style={{ fontSize: 12, color: 'var(--dorado-claro, #FFD166)', marginTop: 4 }}>👑 Precio Fundador: {status.preciosFundador.descuento}% off tu primer mes</div>}
                      </>
                  }
                </div>
                <ul className="plan-features">
                  {plan.features.map(f => <li key={f}><span className="feature-check">✓</span>{f}</li>)}
                </ul>

                {isActive ? (
                  <div className="plan-cta-active">Plan activo ✅</div>
                ) : isUpgrade ? (
                  <button
                    className={`btn ${plan.popular ? 'btn-primary' : 'btn-secondary'} plan-cta`}
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={loading}
                  >
                    {loading ? '⏳ Procesando...' : `⬆️ Mejorar a ${plan.nombre}`}
                  </button>
                ) : isDowngrade && plan.id !== 'free' ? (
                  <button
                    className="btn btn-outline plan-cta"
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={loading}
                  >
                    {loading ? '⏳ Procesando...' : `⬇️ Cambiar a ${plan.nombre}`}
                  </button>
                ) : plan.id === 'free' && user?.plan !== 'free' ? (
                  <button
                    className="btn btn-outline plan-cta"
                    style={{ borderColor: 'var(--texto-muted)', color: 'var(--texto-muted)' }}
                    onClick={handleDowngrade}
                  >
                    🌱 Bajar a gratuito
                  </button>
                ) : null}

                {(plan.id === 'mate' || plan.id === 'premium') && (
                  <p className="plan-mp-note">
                    Pago seguro con <strong>MercadoPago</strong> · Tarjeta, débito, Pago Fácil
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {compras?.productos?.length > 0 && (
          <div style={{ marginTop: 40 }}>
            <h2 className="regalos-section-title">✨ Compras sueltas</h2>
            <p className="regalos-sub" style={{ marginBottom: 16 }}>Un empujón cuando lo necesitás, con cualquier plan. Se paga una sola vez.</p>
            <div className="compras-grid">
              {compras.productos.map(p => (
                <div key={p.id} className="compra-card">
                  <div className="compra-nombre">{ICONOS_COMPRA[p.id]} {p.nombre}</div>
                  <div className="compra-desc">{p.descripcion}</div>
                  {activoCompra(p.id) && <div className="compra-activa">✅ {activoCompra(p.id)}</div>}
                  <div className="compra-precio">{fmtARS(p.precio)}</div>
                  <button className="btn btn-primary btn-sm" disabled={!!comprando} onClick={() => handleComprar(p.id)}>
                    {comprando === p.id ? 'Redirigiendo…' : 'Comprar'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="sub-footer">
          <p>🔒 Pagos 100% seguros con MercadoPago · Cancelás cuando quieras · Sin contratos</p>
        </div>

        {/* ── Sección gestión de cuenta ── */}
        <div className="account-management-section">
          <h2 className="account-mgmt-title">⚙️ Gestión de cuenta</h2>
          <div className="account-mgmt-cards">
            <div className="account-mgmt-card">
              <div className="account-mgmt-icon">📣</div>
              <div>
                <div className="account-mgmt-label">Reportar un problema</div>
                <div className="account-mgmt-desc">¿Encontraste un error o falla en la app? Contanos y lo solucionamos.</div>
              </div>
              <button className="btn btn-sm btn-outline" onClick={() => setShowReportModal(true)}>
                Reportar
              </button>
            </div>

            {user?.plan !== 'free' && (
              <div className="account-mgmt-card">
                <div className="account-mgmt-icon">🌱</div>
                <div>
                  <div className="account-mgmt-label">Bajarme al plan gratuito</div>
                  <div className="account-mgmt-desc">Cancelás tu suscripción y pasás al plan gratuito inmediatamente.</div>
                </div>
                <button
                  className="btn btn-sm"
                  style={{ borderColor: 'var(--rojo)', color: 'var(--rojo-claro)', background: 'rgba(230,57,70,0.08)', border: '1px solid' }}
                  onClick={handleDowngrade}
                >
                  Dar de baja
                </button>
              </div>
            )}

            <div className="account-mgmt-card account-mgmt-danger">
              <div className="account-mgmt-icon">🗑️</div>
              <div>
                <div className="account-mgmt-label">Eliminar mi cuenta</div>
                <div className="account-mgmt-desc">Borra permanentemente tu perfil, conexiones, mensajes y todos tus datos.</div>
              </div>
              <button
                className="btn btn-sm"
                style={{ background: 'rgba(230,57,70,0.15)', color: 'var(--rojo-claro)', border: '1px solid rgba(230,57,70,0.3)' }}
                onClick={() => setShowDeleteModal(true)}
              >
                Eliminar cuenta
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
